import { motion, useReducedMotion } from "motion/react";
import { EASE_OUT } from "../../kit.jsx";

// Line-art marks for each guide. Every stroke draws in once, in order, when the hero mounts.

const GLYPH_PATHS = {
  // Find: a lens over a field of friction marks, one of them circled.
  a: [
    "M 26 58 h 18 M 66 58 h 18 M 106 58 h 18 M 146 58 h 18 M 186 58 h 18",
    "M 26 96 h 18 M 66 96 h 18 M 146 96 h 18 M 186 96 h 18",
    "M 26 134 h 18 M 66 134 h 18 M 106 134 h 18 M 186 134 h 18",
    "M 26 172 h 18 M 106 172 h 18 M 146 172 h 18 M 186 172 h 18",
    "M 104 96 m -26 0 a 26 26 0 1 0 52 0 a 26 26 0 1 0 -52 0",
    "M 115 96 m -38 0 a 38 38 0 1 0 76 0 a 38 38 0 1 0 -76 0",
    "M 142 124 L 186 172",
  ],
  // Frame: one sentence split into person, moment, consequence, constraint.
  b: [
    "M 20 84 v -20 h 8 M 20 84 v 20 h 8",
    "M 60 64 h 8 v 40 h -8",
    "M 80 84 v -20 h 8 M 80 84 v 20 h 8",
    "M 124 64 h 8 v 40 h -8",
    "M 144 84 v -20 h 8 M 144 84 v 20 h 8",
    "M 188 64 h 8 v 40 h -8",
    "M 32 84 h 22 M 92 84 h 26 M 156 84 h 26",
    "M 20 148 h 200",
    "M 20 176 h 132",
    "M 164 166 v 22",
  ],
  // Team up: a clock with checkpoints and the halfway demo marked.
  c: [
    "M 120 120 m -84 0 a 84 84 0 1 0 168 0 a 84 84 0 1 0 -168 0",
    "M 120 44 v 14 M 196 120 h -14 M 120 196 v -14 M 44 120 h 14",
    "M 120 120 L 120 72",
    "M 120 120 L 156 142",
    "M 120 196 m -9 0 a 9 9 0 1 0 18 0 a 9 9 0 1 0 -18 0",
    "M 198 44 l 14 -14 M 212 30 h -12 M 212 30 v 12",
  ],
  // Test: five small screens linked in one flow.
  d: [
    "M 18 70 h 44 a 8 8 0 0 1 8 8 v 64 a 8 8 0 0 1 -8 8 h -44 a 8 8 0 0 1 -8 -8 v -64 a 8 8 0 0 1 8 -8 z",
    "M 70 110 h 22",
    "M 108 40 h 44 a 8 8 0 0 1 8 8 v 44 a 8 8 0 0 1 -8 8 h -44 a 8 8 0 0 1 -8 -8 v -44 a 8 8 0 0 1 8 -8 z",
    "M 108 140 h 44 a 8 8 0 0 1 8 8 v 44 a 8 8 0 0 1 -8 8 h -44 a 8 8 0 0 1 -8 -8 v -44 a 8 8 0 0 1 8 -8 z",
    "M 92 110 V 70 h 8 M 92 110 V 170 h 8",
    "M 160 70 h 22 V 110 M 160 170 h 22 V 110",
    "M 182 110 h 40",
    "M 116 60 h 28 M 116 72 h 18 M 116 160 h 28 M 116 172 h 18",
  ],
  // Show: a lectern, a voice and a clear next step.
  e: [
    "M 44 200 h 100",
    "M 68 200 V 124 h 52 V 200",
    "M 58 124 L 64 104 h 60 l 6 20",
    "M 150 92 a 18 18 0 0 1 0 36",
    "M 164 76 a 36 36 0 0 1 0 68",
    "M 178 60 a 54 54 0 0 1 0 100",
    "M 94 104 v -40",
    "M 94 64 m -12 0 a 12 12 0 1 0 24 0 a 12 12 0 1 0 -24 0",
  ],
};

export function GuideGlyph({ variant = "a", className = "", delay = 0.2, onView = false }) {
  const reduceMotion = useReducedMotion();
  const paths = GLYPH_PATHS[variant] ?? GLYPH_PATHS.a;
  return (
    <svg className={`lb-glyph ${className}`} viewBox="0 0 240 240" aria-hidden="true" focusable="false">
      {paths.map((d, index) => (
        <motion.path
          key={d}
          d={d}
          initial={reduceMotion ? false : { pathLength: 0, opacity: 0 }}
          {...(onView
            ? { whileInView: { pathLength: 1, opacity: 1 }, viewport: { once: true, amount: 0.5 } }
            : { animate: { pathLength: 1, opacity: 1 } })}
          transition={{
            pathLength: { duration: 0.9, delay: delay + index * 0.12, ease: EASE_OUT },
            opacity: { duration: 0.01, delay: delay + index * 0.12 },
          }}
        />
      ))}
    </svg>
  );
}
